import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, XCircle, StopCircle, ListChecks } from "lucide-react";
import { useWsNavigate, WorkspaceLink } from "../../hooks/useWsNavigate";
import { useAuth } from "../../contexts/AuthContext";
import { stressTestsAPI } from "../../services/api/stressTests";
import StatusBadge from "../../components/ui/StatusBadge";
import LoadSummary from "../../components/stress-test/LoadSummary";

// Extra StatusBadge color entries for the stress-test lifecycle. `pending`,
// `completed` and `failed` already exist in StatusBadge's own STATUS_CONFIG;
// these fill in the stress-test-specific phases in between.
const STRESS_TEST_STATUS_CONFIG = {
  provisioning: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500", label: "Provisioning" },
  warming: { bg: "bg-sky-50", text: "text-sky-700", dot: "bg-sky-500", label: "Health Check" },
  ramping: { bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500", label: "Ramping Up" },
  sustaining: { bg: "bg-indigo-50", text: "text-indigo-700", dot: "bg-indigo-500", label: "Sustaining Load" },
  draining: { bg: "bg-violet-50", text: "text-violet-700", dot: "bg-violet-500", label: "Draining" },
  teardown: { bg: "bg-slate-50", text: "text-slate-700", dot: "bg-slate-500", label: "Wrapping Up" },
  aborted: { bg: "bg-orange-50", text: "text-orange-700", dot: "bg-orange-500", label: "Stopped" },
};

// The health check (StressTests::HealthCheck::HEALTH_CALL_COUNT calls, 1 as
// of writing) runs before the bulk load launches. `pending` and
// `provisioning` are pre-health-check setup phases; `warming` is the health
// check itself. All three share the same "checking your bot" UI.
const HEALTH_CHECK_STATUSES = ["pending", "provisioning", "warming"];

// Once the health check passes, the bulk load runs through these phases.
const LOAD_STATUSES = ["ramping", "sustaining", "draining", "teardown"];

const TERMINAL_STATUSES = ["completed", "failed", "aborted"];

const HEALTH_CHECK_MESSAGES = {
  pending: "Preparing your stress test…",
  provisioning: "Provisioning test infrastructure…",
  warming: "Running 1 health call to check your bot…",
};

// Reassurance that the run is server-side — the user doesn't have to babysit
// this tab. The test is dispatched to background jobs; the page only polls to
// show progress.
const BackgroundNote = () => (
  <p className="mt-3 text-xs text-muted-foreground">
    This runs in the background — you can close this tab or navigate away and it
    keeps going. Come back any time, or open the batch to watch calls live.
  </p>
);

const StressTestResults = () => {
  const { id } = useParams();
  const navigate = useWsNavigate();
  const { currentWorkspace } = useAuth();
  const clientId = currentWorkspace?.id;

  const [stressTest, setStressTest] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aborting, setAborting] = useState(false);

  const fetchStressTest = useCallback(
    async (silent = false) => {
      if (!clientId || !id) return;
      try {
        if (!silent) setLoading(true);
        const response = await stressTestsAPI.getStressTest(id, clientId);
        setStressTest(response?.data?.stress_test || null);
        setReport(response?.data?.report || null);
        setError(null);
      } catch (err) {
        setError("Failed to load stress test status.");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id, clientId]
  );

  // Initial fetch.
  useEffect(() => {
    fetchStressTest();
  }, [fetchStressTest]);

  // Poll every 5s while the status is non-terminal. Mirrors the auto-refresh
  // pattern in BatchRunDetails: the interval is re-armed whenever `stressTest`
  // changes and torn down (no re-arm) once the status is terminal, so there
  // is never more than one live interval and nothing polls past completion.
  useEffect(() => {
    if (!stressTest) return undefined;
    if (TERMINAL_STATUSES.includes(stressTest.status)) return undefined;

    const timer = setInterval(() => {
      fetchStressTest(true);
    }, 5000);
    return () => clearInterval(timer);
  }, [stressTest, fetchStressTest]);

  const status = stressTest?.status;
  const isHealthCheck = HEALTH_CHECK_STATUSES.includes(status);
  const isLoadRunning = LOAD_STATUSES.includes(status);
  const isFailed = status === "failed";
  const isCompleted = status === "completed";
  const isAborted = status === "aborted";
  const isRunning = isHealthCheck || isLoadRunning;

  const handleAbort = async () => {
    if (!window.confirm("Stop this stress test? In-flight calls are ended; results so far are kept.")) return;
    setAborting(true);
    try {
      const res = await stressTestsAPI.abortStressTest(id, clientId);
      setStressTest(res?.data?.stress_test || null);
      setReport(res?.data?.report || null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to stop the stress test.");
    } finally {
      setAborting(false);
    }
  };

  return (
    <div className="px-4 max-w-3xl mx-auto">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-foreground">Stress Test</h1>
        <div className="flex items-center gap-3">
          {stressTest?.batch_id && (
            <WorkspaceLink
              to={`/batch-run/${stressTest.batch_id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
            >
              <ListChecks className="h-4 w-4" />
              Go to batch
            </WorkspaceLink>
          )}
          {isRunning && (
            <button
              type="button"
              onClick={handleAbort}
              disabled={aborting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
            >
              <StopCircle className="h-4 w-4" />
              {aborting ? "Stopping…" : "Stop test"}
            </button>
          )}
          {status && <StatusBadge status={status} config={STRESS_TEST_STATUS_CONFIG} />}
        </div>
      </div>

      {loading && !stressTest && (
        <div className="bg-card border border-border rounded-lg p-6 text-center text-sm text-muted-foreground">
          Loading stress test…
        </div>
      )}

      {error && !stressTest && (
        <div className="bg-card border border-destructive/30 rounded-lg p-6 text-center text-sm text-destructive">
          {error}
        </div>
      )}

      {stressTest && (
        <div className="bg-card border border-border rounded-lg p-6">
          {isHealthCheck && (
            <div className="text-center py-6">
              <Loader2 className="h-10 w-10 text-primary animate-spin mx-auto mb-4" />
              <h2 className="text-base font-semibold text-foreground mb-1">
                {HEALTH_CHECK_MESSAGES[status] || HEALTH_CHECK_MESSAGES.pending}
              </h2>
              <p className="text-sm text-muted-foreground">
                The full load only launches once these calls succeed.
              </p>
              <BackgroundNote />
            </div>
          )}

          {isFailed && (
            <div className="text-center py-6">
              <XCircle className="h-10 w-10 text-destructive mx-auto mb-4" />
              <h2 className="text-base font-semibold text-foreground mb-1">
                Stress Test Failed
              </h2>
              <p className="text-sm text-destructive">
                {stressTest.target_not_met_reason || "The stress test did not complete successfully."}
              </p>
            </div>
          )}

          {isLoadRunning && (
            <div className="text-center py-6">
              <Loader2 className="h-10 w-10 text-primary animate-spin mx-auto mb-4" />
              <h2 className="text-base font-semibold text-foreground mb-1">
                Load test in progress
              </h2>
              <p className="text-sm text-muted-foreground">
                {stressTest.achieved_concurrency ?? 0} / {stressTest.target_concurrency ?? "—"} concurrent calls
              </p>
              <BackgroundNote />
            </div>
          )}

          {isCompleted && (
            <div className="text-center py-6">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-base font-semibold text-foreground mb-1">Completed</h2>
              <p className="text-sm text-muted-foreground">
                {stressTest.achieved_concurrency ?? 0} / {stressTest.target_concurrency ?? "—"} concurrent calls
              </p>
            </div>
          )}

          {isAborted && (
            <div className="text-center py-6">
              <StopCircle className="h-10 w-10 text-orange-500 mx-auto mb-4" />
              <h2 className="text-base font-semibold text-foreground mb-1">Stopped</h2>
              <p className="text-sm text-muted-foreground">
                You stopped this test. Results from the calls that finished are shown below.
              </p>
            </div>
          )}

          <div data-testid="load-summary-slot">
            {(isCompleted || isAborted) && <LoadSummary report={report} batchId={stressTest.batch_id} />}
          </div>
        </div>
      )}
    </div>
  );
};

export default StressTestResults;
